import type { ListingType } from "../directory-listing.types";

/**
 * Maps a directory `listingType` to the Algolia `type` facet value used to
 * scope an InstantSearch instance to a single document type. Keys match the
 * Sanity `_type` values written by the indexer in `packages/algolia`. Every
 * directory listing type is Algolia-backed.
 */
export const LISTING_TYPE_TO_ALGOLIA_TYPE: Record<ListingType, string> = {
  stores: "store",
  foodDrink: "foodDrink",
  events: "event",
  hotels: "hotel",
  attractions: "attraction",
  articles: "article",
  shoppingGuides: "shoppingGuide",
  vendors: "vendor",
};

/**
 * Whether date-browsing directories query their date-sorted replica index for
 * chronological ordering. Algolia can only sort by an attribute via a replica,
 * which must be provisioned on the Algolia app (the `replicas` config in
 * `packages/algolia` `configure-indices.ts` + a settings sync with an admin
 * key, plus a reindex to populate the numeric sort fields). Until then these
 * types use the primary index's default ranking (`desc(updatedAt)`); flip this
 * to `true` once the replicas exist — VAW-927 / VAW-928 follow-up.
 */
export const DATE_REPLICAS_ENABLED = true;

/**
 * Replica index suffix per listing type (see configure-indices.ts). Events
 * default to the ascending start-date replica so the default "upcoming" view
 * lists the soonest event first; the "past" view switches to the descending
 * replica at runtime (see {@link getEventTimeIndexSuffix}).
 */
const DATE_REPLICA_SUFFIX: Partial<Record<ListingType, string>> = {
  events: "_startdate_asc",
  articles: "_published_desc",
};

/** Time-filter value an event directory can be scoped to. */
type EventTimeFilter = "upcoming" | "past";

/**
 * Start-date replica suffix for an event time filter: upcoming sorts ascending
 * (soonest first), past sorts descending (most recent first).
 */
export function getEventTimeIndexSuffix(timeFilter: EventTimeFilter): string {
  return timeFilter === "past" ? "_startdate_desc" : "_startdate_asc";
}

/** The Algolia index for an event directory, scoped to a time filter. */
export function getEventTimeIndexName(
  baseIndex: string,
  timeFilter: EventTimeFilter,
): string {
  return DATE_REPLICAS_ENABLED
    ? `${baseIndex}${getEventTimeIndexSuffix(timeFilter)}`
    : baseIndex;
}

/**
 * The Algolia index a directory listing should query. Date-browsing types use
 * their date-sorted replica when enabled; everything else uses the locale index.
 */
export function getDirectoryIndexName(
  baseIndex: string,
  listingType: ListingType,
): string {
  const suffix = DATE_REPLICA_SUFFIX[listingType];
  if (suffix && DATE_REPLICAS_ENABLED) {
    return `${baseIndex}${suffix}`;
  }
  return baseIndex;
}

/**
 * Algolia facet attribute backing each directory `FilterGroup` key (the keys
 * declared in `LISTING_TYPE_CONFIGS[type].filterGroups`). Group labels and
 * ordering come from that config; this only maps a group to its index facet.
 */
export const GROUP_KEY_TO_ATTRIBUTE: Record<string, string> = {
  districts: "district",
  cuisines: "cuisines",
  dietaryRequirements: "dietaryReqs",
  ratings: "foodRatings",
  attractionTypes: "attractionTypes",
  amenities: "amenities",
  hotelRatings: "starRating",
  eventTypes: "eventTypes",
  markets: "markets",
};

/** Algolia facet backing the A–Z alphabet bar (uppercased title first letter). */
export const FIRST_LETTER_ATTRIBUTE = "firstLetter";

/**
 * Optional facet-value → label transform per group key. `starRating` is indexed
 * as a number (1–5), so render it as "N Star(s)" to match the GROQ labels.
 */
export const GROUP_FACET_LABEL: Record<string, (value: string) => string> = {
  hotelRatings: (value) => `${value} ${value === "1" ? "Star" : "Stars"}`,
};

/** The unified category facet, shown as the dialog's "Categories" section. */
export const CATEGORIES_ATTRIBUTE = "categories";

// ---------------------------------------------------------------------------
// URL ⇄ facet mapping (preserves the GROQ directory's `?param=<sanityId>`
// contract). Algolia facets on resolved taxonomy NAMES, but the URLs (and the
// links across the site) use Sanity taxonomy document `_id`s — so we fetch an
// id↔name map per type (see directory-facet-maps) and translate.
// ---------------------------------------------------------------------------

/** Sanity taxonomy `_type` backing each filter-group facet attribute. */
export const FACET_ATTRIBUTE_TO_TAXONOMY: Record<string, string> = {
  district: "district",
  cuisines: "cuisine",
  dietaryReqs: "dietaryRequirement",
  foodRatings: "rating",
  attractionTypes: "attractionType",
  eventTypes: "eventType",
  amenities: "amenity",
  markets: "market",
  // `starRating` is not a taxonomy — the URL uses "N-star(s)" (the hotel rating
  // field) and is handled specially. `categories` is per-listing-type, below.
};

/** Sanity category `_type` backing the unified `categories` facet, per type. */
export const LISTING_TYPE_TO_CATEGORY_TAXONOMY: Partial<
  Record<ListingType, string>
> = {
  stores: "storeCategory",
  foodDrink: "foodDrinkCategory",
  events: "eventCategory",
  hotels: "hotelCategory",
  attractions: "attractionCategory",
  articles: "articleCategory",
  vendors: "vendorCategory",
  // shoppingGuides has no category facet.
};

/** URL query param for the category filter (matches the GROQ contract). */
export const CATEGORY_PARAM = "category";
